// Library imports
import styled from "styled-components";
import { Row, Col, Spin } from "antd";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import store, { RootState } from "store";

// Local imports
import Input from "../../../Common/Components/Input";
import DownAngle from "../../../../assets/img/icons/DownAngle.svg";
import Cross from "../../../../assets/img/icons/cross.svg";
import { closeCreateNewContactSidebar, closeEditContactSidebar, GLOBAL_ACTION } from "modules/GlobalActions";
import { createNewContactAudience, createNewContactResetFormAudience, updateContact } from "../actions";
import validator from "validator";
import produce from "immer";
import Message from "modules/Common/Components/Message";
import FadeIn from "react-fade-in";
import { PEOPLE_AUDIENCE } from "constants/actionTypes";
import { validateName } from "utils";
import EditContactFormLoader from "modules/Common/Components/EditContactFormLoader";
import ButtonRightIcon from "modules/Common/Components/ButtonRightIcon";
import { BASE_PATH_UNSUB } from "constants/index";
import { resetAudiencePeopleTabData } from "../Lists/actions";
import { AUDIENCE_LISTS_DETAILS } from "constants/types";
import { closeEditContactSidePanelAction } from "modules/Audience/Actions/globalContanctsActions";

export interface ISetNewContact {
  selectedTab?: string;
  detailProfileAsider?: boolean;
  showEditContactAsiderHandler?: (value: boolean) => void;
  editSidebar?: boolean;
  setCloseSidebarInstantly?: (value: boolean) => void;
  listId?: number;
  handleClose?: () => void;
}

const CreateContactSidebar: React.FC<ISetNewContact> = (props: any) => {
  // Local States
  const {
    selectedTab,
    detailProfileAsider,
    showEditContactAsiderHandler,
    editSidebar,
    setCloseSidebarInstantly,
    listId,
    handleClose,
  } = props;
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [firstTimeLoad, setFirstTimeLoad] = useState(true);
  const [fName, setFName] = useState("");
  const [lName, setLName] = useState("");
  const [email, setEmail] = useState("");
  const [pNumber, setPNumber] = useState("");
  const [role, setRole] = useState("");
  const [company, setCompany] = useState("");
  const [domain, setDomain] = useState("");
  const [errors, setErrors] = useState({
    fName: false,
    lName: false,
    email: false,
    pNumber: false,
    role: false,
    company: false,
    domain: false,
  });
  const [errorStatus, setErrorStatus]: any = useState({
    status: false,
    message: "",
    errors,
  });
  const [showUpdatedContactMessage, setShowUpdatedContactMessage] = useState(false);
  const [showAddedContactMessage, setShowAddedContactMessage] = useState(false);
  const [loading, setLoading] = useState(false);

  // Redux States
  const creatingNewContact: boolean = useSelector(
    (state: RootState) => state.peopleAudience.createNewContactLoading
  );
  const updateContactInProgress: boolean = useSelector(
    (state: RootState) => state.peopleAudience.updateContactInProgress
  );
  const contactUpdatedSuccessfully: boolean = useSelector(
    (state: RootState) => state.peopleAudience.contactUpdatedSuccessfully
  );
  const selectedPersonalityTypes: string[] = useSelector(
    (state: RootState) => state.peopleAudience.selectedPersonalityTypes
  );
  const audienceFiltersApplied: boolean = useSelector(
    // this state shows if the user has applied the filters or not on audience people
    (state: RootState) => state.uiReducer.audienceFiltersApplied
  );
  const showCreateContactForm = useSelector((state: RootState) => state.audience.showCreateContactForm);
  const contactUpdateFailed: boolean = useSelector((state: RootState) => state.peopleAudience.contactUpdateFailed);
  const contactDetails = useSelector((state: RootState) => state.peopleAudience.contactDetails);
  const contactDetailsLoading = useSelector((state: RootState) => state.peopleAudience.contactDetails.loading);
  const isFormSubmitted: boolean = useSelector((state: RootState) => state.peopleAudience.createNewContactStatus);
  const organizationsReducerState = useSelector((state: RootState) => state.peopleAudience.organizations);
  const designationsReducerState = useSelector((state: RootState) => state.peopleAudience.designations);
  const contactUpdatedInList = useSelector((state: RootState) => state.audienceListDetails.contactUpdated);
  const contactUpdateErrorMessage = useSelector((state: RootState) => state.audienceListDetails.contactUpdateError);
  const contactUpdatedInPeopleAudience = useSelector(
    (state: RootState) => state.peopleAudience.contactUpdatedInPeopleAudience
  );
  const searchTermPeople = useSelector((state: RootState) => state.peopleAudience.searchTermPeople);
  const searchTermListDetails = useSelector((state: RootState) => state.audienceListDetails.searchTermListDetails);
  const contactAddedInList: boolean = useSelector(
    (state: RootState) => state.audienceListDetails.contactAddedInList
  );

  useEffect(() => {
    if (contactUpdateErrorMessage) {
      setErrorStatus(
        produce((draft: any) => {
          draft.status = true;
          draft.message = contactUpdateErrorMessage;
          draft.errors.email = true;
        })
      );
      store.dispatch({
        type: AUDIENCE_LISTS_DETAILS.RESET_CONTACT_FAILED_TO_UPDATE,
      });
    }
  })

  // Component Logic
  useEffect(() => {
    if (contactUpdatedInList) {
      setShowUpdatedContactMessage(true);
      store.dispatch({
        type: AUDIENCE_LISTS_DETAILS.RESET_CONTACT_UPDATED_SUCCESS,
      });
      setTimeout(() => {
        setShowUpdatedContactMessage(false);
      }, 3000);
    }
  }, [contactUpdatedInList]);

  useEffect(() => {
    if (contactAddedInList) {
      resetForm();
      setShowAddedContactMessage(true);
      setLoading(false);
      setTimeout(() => {
        setShowAddedContactMessage(false);
      }, 3000);
      store.dispatch({
        type: AUDIENCE_LISTS_DETAILS.RESET_NEWLY_ADDED_CONTACT,
      });
    }
  }, [contactAddedInList]);

  const createNewContactAudienceMethod = () => {
    if (creatingNewContact === true) {
    } else {
      formSubmit();
    }
  };

  useEffect(() => {
    if (!showCreateContactForm) resetForm();
    if (!showEditContactAsiderHandler) resetForm();
  }, [showCreateContactForm, showEditContactAsiderHandler]);

  useEffect(() => {
    if (errorStatus.errors.fName || errorStatus.errors.lName || errorStatus.errors.email ||
      errorStatus.errors.pNumber || errorStatus.errors.role || errorStatus.errors.company || errorStatus.errors.domain) {
      setLoading(false);
    }
  }, [errorStatus.errors]);

  useEffect(() => {
    if (contactUpdatedSuccessfully) {
      setLoading(false);
    }
  }, [contactUpdatedSuccessfully])

  useEffect(() => {
    if (showSuccessMessage) {
      setLoading(false);
      setTimeout(() => {
        setShowSuccessMessage(false);
        store.dispatch({
          type: PEOPLE_AUDIENCE.RESET_CONTACT_UPDATE_FLAG,
        });
      }, 3500);
    }
  }, [showSuccessMessage]);

  const CheckIsValidDomain = (domain: any) => {
    let re = new RegExp(/^((?:(?:(?:\w[\.\-\+]?)*)\w)+)((?:(?:(?:\w[\.\-\+]?){0,62})\w)+)\.(\w{2,6})$/);
    return domain.match(re);
  };
  // const validDomain = new RegExp(
  //   /^((?:(?:(?:w[.-+]?)*)w)+)((?:(?:(?:w[.-+]?){0,62})w)+).(w{2,6})$/
  // );
  // const validEmail = new RegExp("^[a-zA-Z0-9._:$!%-]+@[a-zA-Z0-9.-]+.[a-zA-Z]$");

  const validateUrl = (domain: any) => {
    try {
      var subdirectory;
      var sub = domain.split("/");
      if (domain.startsWith("http") || domain.startsWith("https")) {
        subdirectory = sub[3];
      } else {
        subdirectory = sub[1];
      }
      var r = new RegExp(/^(http(s)?:\/\/)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/);
      var isValid = r.test(domain);
      if (subdirectory) {
        return false;
      }
      return isValid === true;
    } catch (error) {
      return false;
    }
  }
  const resetForm = () => {
    setFName("");
    setLName("");
    setEmail("");
    setPNumber("");
    setRole("");
    setCompany("");
    setDomain("");
    setErrorStatus({
      status: false,
      message: "",
      errors,
    });
  };

  useEffect(() => {
    resetForm();
    if (!firstTimeLoad) {
      setShowSuccessMessage(true);
    } else {
      setFirstTimeLoad(false);
    }

    return () => {
      if (showEditContactAsiderHandler) return;
      resetForm();
      store.dispatch({
        type: PEOPLE_AUDIENCE.RESET_EDIT_CONTACT,
      });
    };
  }, [isFormSubmitted]);

  useEffect(() => {
    const { first_name, last_name, contact_email, phone_number, designation, organization, company_website } =
      contactDetails;
    // console.log("CONTACT DETAILS: ", contactDetails);
    if (detailProfileAsider || editSidebar) {
      if (first_name == null) setFName("");
      else setFName(first_name);
      if (last_name == null) setLName("");
      else setLName(last_name);
      if (contact_email == null) setEmail("");
      else setEmail(contact_email);
      if (phone_number == null) setPNumber("");
      else setPNumber(phone_number);
      if (designation == null) setRole("");
      else setRole(designation);
      if (organization == null) setCompany("");
      else setCompany(organization);
      if (company_website == null) setDomain("");
      else setDomain(company_website);
    }
  }, [contactDetails]);

  const formSubmit = () => {
    setLoading(true);
    setErrorStatus(
      produce((draft: any) => {
        draft.status = false;
        draft.message = "";
        draft.errors.fName = false;
        draft.errors.lName = false;
        draft.errors.email = false;
        draft.errors.pNumber = false;
        draft.errors.role = false;
        draft.errors.company = false;
        draft.errors.domain = false;
      })
    );
    const form = { fName, lName, email, pNumber, role, company, domain, listId };
    if (validator.isEmpty(fName.trim()) || !validateName(fName.trim()) || fName.trim()?.length > 249) {
      setErrorStatus(
        produce((draft: any) => {
          draft.status = true;
          draft.message = "Please enter a valid First Name";
          draft.errors.fName = true;
        })
      );
    } else if (validator.isEmpty(lName.trim()) || !validateName(lName.trim()) || lName.trim()?.length > 249) {
      setErrorStatus(
        produce((draft: any) => {
          draft.status = true;
          draft.message = "Please enter a valid Last Name";
          draft.errors.lName = true;
        })
      );
    } else if (!email.trim().toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      )) {
      setErrorStatus(
        produce((draft: any) => {
          draft.status = true;
          draft.message = "Please enter a valid Email Address";
          draft.errors.email = true;
        })
      );
    } else if (
      (!validator.isEmpty(pNumber.trim()) && !validator.isNumeric(pNumber.trim())) ||
      pNumber.trim().length > 15
    ) {
      setErrorStatus(
        produce((draft: any) => {
          draft.status = true;
          draft.message = "Please enter a valid Phone Number";
          draft.errors.pNumber = true;
        })
      );
    } else if (validator.isEmpty(company.trim())) {
      setErrorStatus(
        produce((draft: any) => {
          draft.status = true;
          draft.message = "Please enter a valid Company Name";
          draft.errors.company = true;
        })
      );
    } else if (!validator.isEmpty(domain.trim()) && !validateUrl(domain)) {
      setErrorStatus(
        produce((draft: any) => {
          draft.status = true;
          draft.message = "Please enter a valid Website, e.g. www.forbes.com";
          draft.errors.domain = true;
        })
      );
    } else if (detailProfileAsider) {
      let addOrganization = true;
      let addDesignation = true;
      organizationsReducerState.length > 0 &&
        organizationsReducerState.map((organization: any) => {
          if (organization.name?.trim() === form.company?.trim()) {
            addOrganization = false;
          }
        });
      designationsReducerState.length > 0 &&
        designationsReducerState.map((designation: any) => {
          if (designation.name?.trim() === form.role?.trim()) {
            addDesignation = false;
          }
        });
      updateContact(form, contactDetails?.contact_id?.toString(), addOrganization, addDesignation);
    } else {
      setCloseSidebarInstantly(false);
      if (listId) form.listId = listId;
      //! need to remove selectedTab and check if it is required
      createNewContactAudience(form, selectedTab, audienceFiltersApplied, searchTermPeople, searchTermListDetails);

      if (audienceFiltersApplied || searchTermPeople.length > 0) {
        resetAudiencePeopleTabData(); // reset people tab data
        store.dispatch({
          type: GLOBAL_ACTION.APPLY_AUDIENCE_FILTERS,
        });
        store.dispatch({
          type: PEOPLE_AUDIENCE.NEW_CONTACT_CREATED_WITH_FILTERS,
        });
        setTimeout(function () {
          store.dispatch({
            type: PEOPLE_AUDIENCE.RESET_CREATE_CONTACT_LOADER,
          });
          closeCreateNewContactSidebar();
          createNewContactResetFormAudience();
        }, 3000);
      }
    }
  };

  const closeCreateNewContactSidebarHandler = () => {
    setCloseSidebarInstantly(true);
    closeCreateNewContactSidebar();
    resetForm();
  };

  return (
    <Wrapper
      detailProfileAsider={detailProfileAsider}
      errorStatus={errorStatus}
      contactUpdatedSuccessfully={contactUpdatedSuccessfully}
      updateContactInProgress={updateContactInProgress}
      contactUpdateFailed={contactUpdateFailed}
      editSidebar={editSidebar}
      loading={loading}
    >
      <div className="header-section">
        <div className="second-row padding-bottom-xl padding-bottom-tab-xl d-flex justify-content-between align-items-center">
          <h4 className={`${detailProfileAsider ? "heading-style" : ""}`}>
            {detailProfileAsider ? "Edit" : "New"}
            <br />
            Contact
          </h4>
          {!detailProfileAsider && (
            <span className="cross" onClick={closeCreateNewContactSidebarHandler}>
              <img src={Cross} alt="" />
            </span>
          )}
          {detailProfileAsider && (
            <div>
              {contactDetails.unsubscribed === true ? (
                <span className="margin-top-xl d-block margin-right-lg if-scroll">
                  <ButtonRightIcon outlineClass="green-outline" label="Unsubscribed" noIcon disable />
                </span>
              ) : (
                <span className="margin-top-xl d-block margin-right-lg if-scroll">
                  <a href={`${BASE_PATH_UNSUB}unsub/${contactDetails?.unsub_id}`} target="_blank" rel="noreferrer">
                    <ButtonRightIcon outlineClass="green-outline" label="Unsubscribe Globally" noIcon />
                  </a>
                </span>
              )}
            </div>
          )}
        </div>
        <div className="mx-3">
          {showUpdatedContactMessage && (
            <FadeIn>
              <Message type="success" label="Success" message={"Existing Contact Updated Successfully"} />
            </FadeIn>
          )}
          {showAddedContactMessage && (
            <FadeIn>
              <Message type="success" label="Success" message={"Contact Added Successfully"} />
            </FadeIn>
          )}
          {errorStatus.status ? (
            <FadeIn>
              <Message type="error" label="Error" message={errorStatus.message} />
            </FadeIn>
          ) : contactUpdatedSuccessfully && !updateContactInProgress ? (
            <FadeIn>
              <Message type="success" label="Success" message={"Contact Updated Successfully"} />
            </FadeIn>
          ) : contactUpdateFailed && !updateContactInProgress ? (
            <FadeIn>
              <Message type="error" label="Failed" message={"Contact Update Failed"} />
            </FadeIn>
          ) : null}
        </div>
      </div>
      {contactDetailsLoading && detailProfileAsider ? (
        <EditContactFormLoader detailProfileAsider={detailProfileAsider} />
      ) : (
        <div className="margin-bottom-md scrollable">
          {loading ? <Spin className="spinner-styles" /> : null}
          {showSuccessMessage && (
            <FadeIn>
              <Message
                type="success"
                label="Success"
                message={`${contactUpdatedInPeopleAudience
                  ? "Existing Contact Updated Successfully"
                  : "Contact Added Successfully"
                  }`}
              />
            </FadeIn>
          )}
          <Row gutter={26}>
            <Col className="gutter-row py-3" span={12}>
              <Input
                className={errorStatus.errors.fName ? "error" : ""}
                type="text"
                placeholder=""
                value={fName}
                onChange={(e: any) => {
                  setFName(e.target.value);
                }}
                onBlur={() => setFName(fName.trim())}
                label="First Name *"
                data-qa="qa-first-name"
              />
            </Col>
            <Col className="gutter-row py-3" span={12}>
              <Input
                className={errorStatus.errors.lName ? "error" : ""}
                label="Last Name *"
                type="text"
                placeholder=""
                value={lName}
                onChange={(e: any) => {
                  setLName(e.target.value);
                }}
                onBlur={() => setLName(lName.trim())}
                data-qa="qa-last-name"
              />
            </Col>
            <Col className="gutter-row py-3" span={24}>
              <Input
                className={errorStatus.errors.email ? "error" : ""}
                type="email"
                placeholder=""
                value={email}
                onChange={(e: any) => {
                  setEmail(e.target.value.trim());
                }}
                label="Email Address *"
                data-qa="qa-email"
              />
            </Col>
            <Col className="gutter-row py-3" span={24}>
              <Input
                className={errorStatus.errors.pNumber ? "error" : ""}
                type="tel"
                placeholder=""
                value={pNumber}
                onChange={(e: any) => {
                  setPNumber(e.target.value.trim().replace(".", ""));
                }}
                label="Phone Number"
                data-qa="qa-phone"
              />
            </Col>
            <Col className="gutter-row py-3" span={24}>
              <Input
                className={errorStatus.errors.role ? "error" : ""}
                type="text"
                placeholder=""
                value={role}
                onChange={(e: any) => {
                  setRole(e.target.value);
                }}
                onBlur={() => setRole(role.trim())}
                label="Job Title"
                data-qa="qa-job-title"
              />
            </Col>
            <Col className="gutter-row py-3" span={24}>
              <Input
                className={errorStatus.errors.company ? "error" : ""}
                type="text"
                placeholder=""
                value={company}
                onChange={(e: any) => {
                  setCompany(e.target.value);
                }}
                onBlur={() => setCompany(company.trim())}
                label="Company Name *"
                data-qa="qa-company"
              />
            </Col>
            <Col className="gutter-row py-3" span={24}>
              <Input
                className={errorStatus.errors.domain ? "error" : ""}
                type="text"
                placeholder=""
                value={domain}
                onChange={(e: any) => {
                  setDomain(e.target.value);
                }}
                onBlur={() => setDomain(domain.trim())}
                label="Company Website"
                data-qa="qa-comp-web"
              />
            </Col>
          </Row>

          <Row gutter={26}>
            <Col className="gutter-row py-3" span={24}>
              {/* <label className="check-label">
              <Checkbox /> This user has given consent for email comunications
            </label> */}
            </Col>
            {/* <Col className="gutter-row py-3" span={24}>
            <div className="show-more active">
              Show more feilds… <img src={ArrowDownGrey} className="close" />
              <img src={ArrowUpGrey} className="open" />
            </div>
          </Col> */}
          </Row>
        </div>
      )}
      <div className="footer-sider hello">
        <span
          onClick={() => {
            if (detailProfileAsider) {
              if (editSidebar) {
                closeEditContactSidebar();
                closeEditContactSidePanelAction();
              } else {
                showEditContactAsiderHandler(false);
              }
            } else closeCreateNewContactSidebar();
            if (setCloseSidebarInstantly) setCloseSidebarInstantly(true);
            resetForm();
          }}
          className="footer-btn mr"
        >
          Cancel
        </span>
        <span
          onClick={() => {
            createNewContactAudienceMethod();
          }}
          className={`footer-btn filled-success ${creatingNewContact || updateContactInProgress ? "disabled" : ""
            } `}
          data-qa="qa-success-btn"
        >
          {detailProfileAsider ? "Update" : "Save"}
        </span>
      </div>
    </Wrapper>
  );
};

export default CreateContactSidebar;

const Wrapper = styled.div<{
  errorStatus: any;
  detailProfileAsider: boolean;
  contactUpdatedSuccessfully: boolean;
  updateContactInProgress: boolean;
  contactUpdateFailed: boolean;
  editSidebar: boolean;
  loading: boolean;
}>`
  padding: 0 20px;
  width: ${(props) => (props.detailProfileAsider ? "500px" : "")};
  position: relative;
  height: 100vh;
  border-left: ${(props) => (props.detailProfileAsider && !props.editSidebar ? "1px solid #dddddd;" : "")};
  opacity: ${(props) => (props.loading ? "0.5" : "1")};
  pointer-events: ${(props) => (props.loading ? "none" : "auto")};
  .spinner-styles{
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 16;
  }
  .heading-style {
    font-family: "Roboto Slab", serif;
    color: #424b50;
    font-size: 30px;
    font-weight: 600;
    line-height: 1;
    margin-top: ${(props) => (!props.editSidebar ? "60px" : "0px")};
    margin-bottom: 0;
  }
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    padding: 0 0px;
    width: ${(props) => (props.detailProfileAsider ? "400px" : "")};
  }
  .second-row {
    padding-left: 20px;
    .if-scroll {
      padding-right: 4px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        padding-left: 0px;
      }
    }
  }
  .main-title {
    color: #67696a;
    font-size: 18px;
    font-weight: 500;
    font-family: "Roboto", sans-serif;
    margin: 0;
  }
  label {
    margin: 0 0 6px 0;
    &.check-label {
      color: #7e7e7e;
      font-weight: 500;
      font-family: "Roboto", sans-serif;
    }
  }
  input {
    padding: 5px 10px;
  }
  .cross {
    cursor: pointer;
    position: absolute;
    top: 20px;
    right: 20px;
  }
  .show-more {
    color: #27ce0a;
    font-size: 18px;
    font-weight: 500;
    font-family: "Roboto", sans-serif;
    cursor: pointer;
    .open {
      display: none;
    }
    .close {
      display: inline;
    }
    &.active {
      .open {
        display: inline;
      }
      .close {
        display: none;
      }
    }
  }
  .select-dropdown {
    width: 100%;
    .ant-select-selector {
      border: 1px solid #dadada;
      border-radius: 8px;
      background-color: #fff;
      color: #7e7e7e;
      font-family: "Roboto", sans-serif;
      font-size: 16px;
      font-weight: 500;
      padding: 5px 13px;
      width: 100%;
      margin-top: 6px;
    }
    .ant-select-arrow {
      background-image: url(${DownAngle});
      background-repeat: no-repeat;
      background-position: 8px 8px;
      border-left: 1px solid #dddddd;
      padding-left: 10px;
      margin-left: 10px;
      min-width: 22px;
      min-height: 22px;
      vertical-align: 0;
      top: 40%;
      span {
        opacity: 0;
      }
    }
  }
  .scrollable {
    /* width: calc(100% + 25px); */
    width: 100%;
    margin-right: -20px;
    padding-right: 20px;
    padding: 0 20px;
    max-height: ${(props) =>
    props.errorStatus.status ||
      (props.contactUpdatedSuccessfully && !props.updateContactInProgress) ||
      (props.contactUpdateFailed && !props.updateContactInProgress)
      ? props.editSidebar
        ? "calc(100vh - 420px)"
        : "calc(100vh - 350px)"
      : props.editSidebar
        ? "calc(100vh - 315px)"
        : "calc(100vh - 270px)"};
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      max-height: ${(props) =>
    props.errorStatus.status ||
      (props.contactUpdatedSuccessfully && !props.updateContactInProgress) ||
      (props.contactUpdateFailed && !props.updateContactInProgress)
      ? props.editSidebar
        ? "calc(100vh - 300px)"
        : "calc(100vh - 305px)"
      : props.editSidebar
        ? "calc(100vh - 215px)"
        : "calc(100vh - 220px)"};
    }
    overflow: auto;
    scrollbar-width: thin;
    scrollbar-color: #001760 #efefef;
    &::-webkit-scrollbar {
      width: 5px;
      height: 5px;
      border-radius: 5px;
    }
    /* Track */
    &::-webkit-scrollbar-track {
      background: #fff;
      margin-top: 10px;
      margin-bottom: 15px;
    }
    /* Handle */
    &::-webkit-scrollbar-thumb {
      background: #001760;
    }
    /* Handle on hover */
    &::-webkit-scrollbar-thumb:hover {
      background: #efefef;
    }
  }
  .footer-sider {
    border-top: 1px solid #e4e4e4;
    display: flex;
    justify-content: flex-end;
    position: absolute;
    bottom: 0px;
    right: 0px;
    margin: 0 20px;
    width: calc(100% - 40px);
    padding: 20px 0;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      /* right: -20px; */
      width: calc(100% - 45px);
    }
    .footer-btn {
      font-size: 16px;
      color: #27ce0a;
      padding: 3px 14px;
      font-family: "helveticaneuebold";
      font-weight: 700;
      border-radius: 17px;
      border: 1.56px solid#27CE0A;
      cursor: pointer;
      text-transform: capitalize;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 12px;
      }
      &.disabled {
        opacity: 0.5;
        pointer-events: none;
      }
      &.mr {
        margin-right: 15px;
      }
      &.filled-success {
        background-color: #27ce0a;
        color: #fff;
      }
    }
  }
`;
